import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import './pages/app-home';
import './pages/app-construction';
import './components/header';
import './styles/global.css';
import { router } from './router';
import { ApiService } from './services/api-service.js';

/**
 * Site-wide holding page. Flip to false to bring the real site back — the
 * router, every page and the portfolio front page are untouched underneath.
 *
 * Bypass for your own browser: visit any page with ?dev=1 once. It sets a
 * localStorage key so the Minecraft instance dashboards, Cloud and Account
 * stay reachable while the public site is parked. ?dev=0 clears it.
 */
const SITE_UNDER_CONSTRUCTION = false;
const BYPASS_KEY = 'teknik_construction_bypass';

function constructionBypassed(): boolean {
  try {
    const flag = new URLSearchParams(window.location.search).get('dev');
    if (flag === '1') localStorage.setItem(BYPASS_KEY, '1');
    if (flag === '0') localStorage.removeItem(BYPASS_KEY);
    return localStorage.getItem(BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

@customElement('app-index')
export class AppIndex extends LitElement {
  @property({ type: String }) currentPage = 'Home';
  @state() userInfo = null;

  static styles = css``;

  async firstUpdated() {
    // Register the route-changed listener BEFORE awaiting any async work.
    // Router fires route-changed via microtask on init, and if we await before
    // adding this listener we miss the very first navigation event — which
    // leaves the page rendering nothing (the bug that gave /books a black screen).
    router.addEventListener('route-changed', () => {
      // Update current page based on the route
      const path = window.location.pathname;
      if (path === '/') {
        this.currentPage = 'Home';
      } else if (path.includes('chat')) {
        this.currentPage = 'Chat';
      } else if (path.includes('tickets')) {
        this.currentPage = 'Tickets';
      } else if (path.includes('form')) {
        this.currentPage = 'New Ticket';
      } else if (path.includes('map')) {
        this.currentPage = 'Map';
      } else if (path.includes('settings')) {
        this.currentPage = 'Settings';
      } else if (path.includes('luna-dashboard')) {
        this.currentPage = 'Luna Dashboard';
      } else if (path.includes('luna')) {
        this.currentPage = 'Luna';
      } else if (path.includes('minecraft-properties')) {
        this.currentPage = 'Properties';
      } else if (path.includes('minecraft')) {
        this.currentPage = 'Minecraft';
      } else if (path.includes('games')) {
        this.currentPage = 'Games';
      } else if (path.includes('stocks')) {
        this.currentPage = 'Stocks';
      } else if (path.includes('store')) {
        this.currentPage = 'Store';
      } else if (path.includes('account')) {
        this.currentPage = 'Account';
      }

      if ("startViewTransition" in document) {
        (document as any).startViewTransition(() => this.requestUpdate());
      }
      else {
        this.requestUpdate();
      }
    });

    // Now that the listener is in place, load user info (safe to await).
    this.loadUserInfo();
  }

  async loadUserInfo() {
    try {
      const authStatus = await ApiService.getAuthStatus();
      if (authStatus.authenticated) {
        this.userInfo = authStatus.user;
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  }

  render() {
    if (SITE_UNDER_CONSTRUCTION && !constructionBypassed()) {
      return html`<app-construction></app-construction>`;
    }

    // One navigation, everywhere: <app-header>, which each page renders. It
    // collapses to a burger and a drawer below 768px, so there is no separate
    // mobile tab bar. A floating pill that showed on some pages, not others,
    // and on top of the header on a third set, was the whole problem.
    return html`
      ${router.render()}
    `;
  }
}
